
import { useEffect, useState } from "react";
import moment from "moment";
import { getDownloadURL, uploadBytesResumable } from "firebase/storage";
import { Table, Modal, Row, Col, Button, Alert, Input, Tag, message, Spin, Select, Progress } from "antd";
import { connect } from "react-redux";
import { DeleteOutlined, EditOutlined, LinkOutlined, DownloadOutlined } from "@ant-design/icons";
import { storage } from "../../config/firebase";
import { fetchPdfs } from "../../store/actions/pdfsActions";
import { fetchTags } from "../../store/actions/tagsActions";
import { clearSearchResults, fetchTracks, searchTrack } from "../../store/actions/trackActions";
import {
  createMuxURLUpload,
  createVideo,
  deleteVideo,
  editVideo,
  getMuxAssetByUploadId,
  getMuxPlaybackUrl,
  getVideos,
  getVideoDownloadInfo,
  downloadVideo,
} from "../../store/actions/videoActions";
import { uploadOtherFilesToCloudflare } from "../../utils/uploadFileToCloudflare";
import { convertHlsToMp4Simple, isHlsToMp4Supported } from "../../utils/hlsToMp4Converter";
import { getHlsStrategy, isSafari, getBrowserDisplayName } from "../../utils/browserDetection";
import DeleteForm from "./components/DeleteForm";
import VideoForm from "./components/VideoForm";
import "./video.scss";

const { Search } = Input;

// eslint-disable-next-line
const Videos = (props) => {
  const {
    createVideo,
    editVideo,
    fetchPdfs,
    fetchTags,
    fetchTracks,
    pdfs,
    tags,
    getVideos,
    deleteVideo,
    videos,
    tracks,
    error,
    user,
    searchTrack,
    clearSearchResults,
    searchResult,
    getVideoDownloadInfo,
    downloadVideo,
  } = props;
  const userLevels = ["admin", "specialist", "sales", "editor"];
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [videoUploadPayload, setVideoUploadPayload] = useState({
    videoUploadError: null,
    videoUploadProgress: 0,
    videoUpload: null,
    videoURL: null,
    isVideoUploadInProgress: false,
  });
  const [difficulty, setDifficulty] = useState("");
  const [name, setName] = useState("");
  const [videoLink, setVideoLink] = useState("");
  const [videoId, setVideoId] = useState("");
  const [description, setDescription] = useState("");
  const [mp4url, setMp4url] = useState("");
  const [time, setTime] = useState("");
  const [thumbnail, setThumbnail] = useState("");
  const [thumbnailURL, setThumbnailURL] = useState("");
  const [videoDBID, setVideoDBID] = useState("");
  const [pdfIds, setPdfIds] = useState([]);
  const [tagIds, setTagIds] = useState([]);
  const [searchText, setSearchText] = useState("");
  const [searchedResults, setSearchedResults] = useState([]);
  const [modalInfo, setModalInfo] = useState({
    name: "Create Video",
    confirmBtnText: "Create",
    type: "createVideo",
  });
  const [muxUrl, setMuxUrl] = useState("");
  const [uploadId, setUploadId] = useState("");
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [availableQualities, setAvailableQualities] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState("");
  const [downloadingVideoId, setDownloadingVideoId] = useState(null);
  const [hlsConversionProgress, setHlsConversionProgress] = useState(0);
  const [isHlsConverting, setIsHlsConverting] = useState(false);
  const [hlsConversionController, setHlsConversionController] = useState(null);
  const [currentVideoName, setCurrentVideoName] = useState("");

  // Function to get the display name for a quality
  const getQualityDisplayName = (quality) => {
    if (quality === "hls") {
      return "HLS (streaming)";
    }
    return quality;
  };

  // Function to get the actual backend value for a quality from display name
  const getQualityBackendValue = (displayName) => {
    if (displayName === "HLS (streaming)") {
      return "hls";
    }
    return displayName;
  };

  useEffect(() => {
    const getData = async () => {
      setLoading(true);
      await Promise.all([getVideos(), fetchPdfs(), fetchTags(), fetchTracks()])
      setLoading(false);
    };
    getData();
  }, [getVideos, fetchPdfs, fetchTags, fetchTracks]);

  useEffect(() => {
    if (searchResult && searchText) {
      setSearchedResults(searchResult.videos || []);
    }

    if (!searchText || searchText === "") {
      setSearchedResults([]);
    }
  }, [searchResult, searchText]);

  const openModal = async (modalMetadata, record) => {
    if (modalMetadata.type === "createLocalUploadVideo") {
      await handleUploadSelect();
    }
    setModalInfo(modalMetadata);
    setIsModalVisible(true);
    if (record) {
      setName(record ? record.name : "");
      setDifficulty(record ? record.difficulty : "");
      setDescription(record ? record.description : "");
      setMp4url(record ? record.mp4Url : "");
      setVideoLink(record ? record["path"] : "");
      setTagIds(record.tags ? record.tags.map((tag) => tag.name) : []);
      setThumbnailURL(record ? record.thumbnail : "");
      setPdfIds(record.pdfs ? record.pdfs.map((tag) => tag.id) : []);
      setVideoDBID(record ? record.id : "");
      setTime(record ? record.time : "");

      setVideoId(record ? record.videoId : "");
      setVideoUploadPayload({
        ...videoUploadPayload,
        videoURL: record ? record["path"] : "",
      });
    }
  };

  const handleInputChange = (e, type) => {
    if (type === "name") {
      setName(e.target.value);
    }

    if (type === "time") {
      setTime(e.target.value);
    }

    if (type === "description") {
      setDescription(e.target.value);
    }

    if (type === "videoId") {
      setVideoId(e.target.value);
    }
  };

  const getFormattedUrl = (videoId) => {
    if (!videoId) return ""; 

    if (videoId.length === 11 && /^[a-zA-Z0-9_-]{11}$/.test(videoId)) {
      return `https://www.youtube.com/watch?v=${videoId}`; 
    }

    if (videoId.includes("youtube.com/watch?v=")) {
      const videoIdFromUrl = videoId.split("v=")[1]?.split("&")[0];
      return `https://www.youtube.com/watch?v=${videoIdFromUrl}`;  
    }

    if (videoId.includes("youtu.be/")) {
      const videoIdFromUrl = videoId.split("youtu.be/")[1];
      return `https://www.youtube.com/watch?v=${videoIdFromUrl}`;  
    }

    if (videoId.includes("/live/")) {
      const videoIdFromUrl = videoId.split("/live/")[1];
      return `https://www.youtube.com/watch?v=${videoIdFromUrl}`;  
    }

    return "";
  };

  const handleUploadSelect = async (uploadType) => {
    const uploadData = await createMuxURLUpload();
    setMuxUrl(uploadData.url);
    setModalInfo({
      name: "Upload video from local computer",
      confirmBtnText: "Create",
      type: "createLocalUploadVideo",
    });
    setUploadId(uploadData.id);
  };

  const customUpload = async ({ onError, onSuccess, file }) => {
    const metadata = {
      contentType: "video/mp4",
    };
    const storageRef = await storage.ref();
    const name = file.name;
    const vfile = storageRef.child(`videos/${name}_${+new Date()}.mp4`);
    try {
      const video = uploadBytesResumable(vfile, file, metadata);
      video.on(
        "state_changed",
        function (snapshot) {
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          var progress =
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setVideoUploadPayload((prevState) => ({
            ...prevState,
            videoUpload: file,
            videoUploadProgress: Math.trunc(progress),
          }));
        },
        function (error) {
          // Handle unsuccessful uploads
        },
        async function () {
          // Handle successful uploads on complete
          // For instance, get the download URL: https://firebasestorage.googleapis.com/...
          const url = await getDownloadURL(video.snapshot.ref);
          setVideoUploadPayload((prevState) => ({
            ...prevState,
            videoUpload: file,
            videoUploadProgress: 100,
            videoURL: url,
          }));
        },
      );
      onSuccess(null, file);
    } catch (e) {
      onError(e);
    }
  };

  const handleSelectChanger = (value, selectType) => {
    if (selectType === "isPdfs") {
      setPdfIds(value);
    }

    if (selectType === "tags") {
      setTagIds(value);
    }

    if (selectType === "difficulty") {
      setDifficulty(value);
    }
  };

  const closeModal = () => {
    setIsModalVisible(false);
    clearState();
  };

  const clearState = () => {
    setName("");
    setDifficulty("");
    setVideoId("");
    setPdfIds([]);
    setTagIds([]);
    setThumbnailURL("");
    setThumbnail("");
    setTime("");
    setVideoLink("");
    setDescription("");
    setVideoDBID("");
    setVideoUploadPayload({
      videoUploadError: null,
      videoUploadProgress: 0,
      videoUpload: null,
      videoURL: null,
      isVideoUploadInProgress: false,
    });
    setMp4url("");
  };

  const getBase64 = (img, callback) => {
    const reader = new FileReader();
    reader.addEventListener("load", () => callback(reader.result));
    reader.readAsDataURL(img);
  };

  const changeThumbnail = (file) => {
    getBase64(file.file.originFileObj, (imageUrl) => {
      setThumbnail(file.file);
      setThumbnailURL(imageUrl);
    });
  };

  const handleOk = async () => {
    setLoading(true);

    const payload = {
      name,
      videoId: getFormattedUrl(videoId),
      time,
      thumbnail: thumbnailURL,
      description,
      tags: tagIds,
      difficulty,
      pdfs: pdfIds,
      uploadType: "localUpload",
    };

    if (modalInfo.type === "createLocalUploadVideo") {
      if (thumbnail) {
        let thumbFileURL = await uploadOtherFilesToCloudflare(
          thumbnail.originFileObj,
          "thumbnails",
        );
        setThumbnailURL(thumbFileURL);

        if (
          name.length > 0 &&
          description.length > 0 &&
          thumbFileURL.length > 0
        ) {
          const assetData = await getMuxAssetByUploadId(uploadId);
          const playbackData = await getMuxPlaybackUrl(assetData.asset_id);
          await createVideo({
            ...payload,
            thumbnail: thumbFileURL,
            path: videoUploadPayload.videoURL,
            uploadId,
            assetId: assetData.asset_id,
            hlsUrl: playbackData.hlsUrl,
          });

          clearState();
          setLoading(false);
          setIsModalVisible(false);
        }
      }
    }

    if (modalInfo.type === "editLocalVideoUpload") {
      if (thumbnail) {
        let thumbFileURL = await uploadOtherFilesToCloudflare(
          thumbnail.originFileObj,
          "thumbnails",
        );
        setThumbnailURL(thumbFileURL);
        payload.thumbnail = thumbFileURL;
      }

      await editVideo(
        { ...payload, path: videoUploadPayload.videoURL },
        videoDBID,
      );
      clearState();
      setLoading(false);
      setIsModalVisible(false);
    }

    if (modalInfo.type === "deleteVideo") {
      await deleteVideo(videoDBID);
    }

    setLoading(false);
    await getVideos();
  };

  const onSearch = async (value) => {
    if (value) {
      clearSearchResults();
      await searchTrack(value);
    }
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchText(value);

    if (!value) {
      setSearchedResults([]);
      clearSearchResults();
    }
  };

  const handleCopyLink = (record) => {
    let videoDetails = null; 

    tracks.some((track) => {
      const module = track.modules.find((module) =>
        module.videos.some((video) => video.id === record.id) 
      );

      if (module) {
        videoDetails = {
          trackName: track.slug,
          trackId: track.id,
          moduleName: module.slug,
        };
        return true;
      }
      return false;
    });

    if (!videoDetails) {
      message.error("Link não encontrado para este vídeo.");
      return;
    }

    const link = `https://app.paradigma.education/trilhas/${videoDetails.trackName}/${videoDetails.moduleName}/${record.slug}?videoTrack=${videoDetails.trackId}&id=${record.id}`
    
   
    navigator.clipboard.writeText(link).then(() => {
      setTimeout(() => 4000);
      message.success("Link copiado com sucesso!");
    }).catch(() => {
      message.error("Error ao copiar o link.")
    });
  };

  const handleDownloadClick = async (record) => {
    console.log("🔍 [Download Debug] Starting download click for video:", record);
    try {
      setDownloadLoading(true);
      setDownloadingVideoId(record.id);
      setCurrentVideoName(record.name);
      
      console.log("🔍 [Download Debug] Calling getVideoDownloadInfo for videoId:", record.id);
      const downloadInfo = await getVideoDownloadInfo(record.id);
      console.log("🔍 [Download Debug] Received downloadInfo:", downloadInfo);
      
      // Fix: Backend returns 'available_qualities' not 'qualities'
      const qualities = downloadInfo?.available_qualities || downloadInfo?.qualities;
      console.log("🔍 [Download Debug] Extracted qualities:", qualities);
      
      if (downloadInfo && qualities && qualities.length > 0) {
        // Convert backend qualities to display names
        const displayQualities = qualities.map(getQualityDisplayName);
        console.log("🔍 [Download Debug] Display qualities:", displayQualities);
        setAvailableQualities(displayQualities);
        setSelectedQuality(displayQualities[0]); // Default to first available quality
        setDownloadModalVisible(true);
        console.log("🔍 [Download Debug] Download modal should now be visible");
      } else {
        console.log("🔍 [Download Debug] No qualities available, showing error");
        message.error("Nenhuma qualidade de download disponível para este vídeo.");
      }
    } catch (error) {
      console.error("🔍 [Download Debug] Error in handleDownloadClick:", error);
      message.error("Erro ao buscar informações de download: " + error.message);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadConfirm = async () => {
    console.log("🔍 [Download Debug] Download confirm clicked");
    console.log("🔍 [Download Debug] Selected quality:", selectedQuality);
    console.log("🔍 [Download Debug] Downloading video ID:", downloadingVideoId);
    
    if (!selectedQuality || !downloadingVideoId) {
      console.log("🔍 [Download Debug] Missing quality or video ID");
      message.error("Por favor, selecione uma qualidade.");
      return;
    }

    try {
      setDownloadLoading(true);
      
      // Convert display quality back to backend value
      const backendQuality = getQualityBackendValue(selectedQuality);
      console.log("🔍 [Download Debug] Backend quality:", backendQuality);
      console.log("🔍 [Download Debug] Calling downloadVideo API...");
      const response = await downloadVideo(downloadingVideoId, backendQuality);
      console.log("🔍 [Download Debug] Download response received:", response);
      
      // Check if response contains HLS stream information
      if (response.data && response.data.is_hls) {
        // Get browser strategy for HLS handling
        const hlsStrategy = getHlsStrategy();
        
        // Check if browser supports HLS to MP4 conversion or if we should use fallback
        if (!isHlsToMp4Supported() || hlsStrategy.shouldUseFallback) {
          console.log('🔍 [Download Debug] Browser strategy suggests fallback:', hlsStrategy);
          
          // Safari and other browsers that don't support captureStream
          if (isSafari()) {
            window.open(response.data.download_url, '_blank', 'noopener,noreferrer');
            message.info(`Safari detectado (${getBrowserDisplayName()}). Stream HLS aberto diretamente para reprodução nativa. Safari tem suporte nativo ao HLS, mas não permite downloads programáticos.`);
          } else {
            window.open(response.data.download_url, '_blank', 'noopener,noreferrer');
            message.warning(`Conversão HLS não é suportada neste navegador (${getBrowserDisplayName()}). Stream HLS aberto em nova aba. Use uma ferramenta que suporte HLS/m3u8 para fazer o download.`);
          }
          
          setDownloadModalVisible(false);
          setDownloadLoading(false);
          return;
        }

        // Start HLS to MP4 conversion
        setIsHlsConverting(true);
        setHlsConversionProgress(0);
        setDownloadModalVisible(false);
        setDownloadLoading(false);

        const filename = `${currentVideoName || 'video'}_${backendQuality}`;
        
        try {
          const controller = await convertHlsToMp4Simple(
            response.data.download_url,
            filename,
            (progress) => {
              setHlsConversionProgress(progress);
            },
            (error) => {
              console.error('HLS conversion error:', error);
              setIsHlsConverting(false);
              setHlsConversionProgress(0);
              
              // Enhanced error message based on error type
              let userMessage = "Erro na conversão do vídeo HLS";
              if (error.message.includes('timeout')) {
                userMessage = "A conversão demorou muito para completar. Tente novamente ou verifique sua conexão com a internet.";
              } else if (error.message.includes('CORS') || error.message.includes('cross-origin')) {
                userMessage = "Erro de CORS: O navegador bloqueou o acesso ao vídeo. Isso é comum com alguns streams HLS.";
              } else if (error.message.includes('network') || error.message.includes('Network')) {
                userMessage = "Erro de rede: Verifique sua conexão com a internet e tente novamente.";
              } else if (error.message.includes('format') || error.message.includes('supported')) {
                userMessage = "Formato de vídeo não suportado pelo navegador para conversão.";
              } else {
                userMessage += ": " + error.message;
              }
              
              message.error(userMessage);
              
              // Fallback to opening in new tab with explanation
              window.open(response.data.download_url, '_blank', 'noopener,noreferrer');
              
              if (isSafari()) {
                message.info(`Fallback para Safari (${getBrowserDisplayName()}): Stream HLS aberto para reprodução nativa. Safari reproduz HLS nativamente, mas conversão falhou.`);
              } else {
                message.info(`Fallback (${getBrowserDisplayName()}): Abrindo stream HLS em nova aba. Use uma ferramenta que suporte downloads de HLS/m3u8 para fazer o download.`);
              }
            },
            () => {
              setIsHlsConverting(false);
              setHlsConversionProgress(100);
              message.success("Conversão e download concluídos com sucesso!");
              setTimeout(() => setHlsConversionProgress(0), 3000);
            }
          );
          
          setHlsConversionController(controller);
        } catch (conversionError) {
          console.error('Failed to start conversion:', conversionError);
          setIsHlsConverting(false);
          setHlsConversionProgress(0);
          
          // Enhanced error handling for conversion startup failures
          let startupErrorMessage = "Falha ao iniciar conversão";
          if (conversionError.message.includes('not supported')) {
            startupErrorMessage = "Conversão HLS não é suportada neste navegador. Use Chrome, Firefox ou Safari mais recentes.";
          } else if (conversionError.message.includes('MediaRecorder')) {
            startupErrorMessage = "Seu navegador não suporta gravação de vídeo. Tente usar um navegador mais recente.";
          } else {
            startupErrorMessage += ": " + conversionError.message;
          }
          
          message.warning(startupErrorMessage);
          
          // Fallback to opening in new tab
          window.open(response.data.download_url, '_blank', 'noopener,noreferrer');
          
          if (isSafari()) {
            message.info(`Safari detectado (${getBrowserDisplayName()}): Stream HLS aberto para reprodução nativa. Para downloads, considere usar ferramentas externas como yt-dlp ou ffmpeg.`);
          } else {
            message.info(`Fallback (${getBrowserDisplayName()}): Stream HLS aberto em nova aba. Para fazer download, use uma ferramenta como yt-dlp, ffmpeg, ou um navegador que suporte downloads de m3u8.`);
          }
        }
        
        return;
      }
      
      // Handle regular MP4 download (for backward compatibility)
      if (response.data instanceof Blob || response.headers['content-type']?.includes('video/')) {
        // Get the filename from the response headers or use a default name
        const contentDisposition = response.headers['content-disposition'];
        let filename = `video_${downloadingVideoId}_${backendQuality}.mp4`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename="(.+)"/);
          if (filenameMatch) {
            filename = filenameMatch[1];
          }
        }
        
        // Create blob URL and trigger download
        const blob = new Blob([response.data], { type: 'video/mp4' });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
        
        message.success("Download iniciado com sucesso!");
      } else {
        message.error("Formato de resposta inesperado.");
      }
      
      setDownloadModalVisible(false);
    } catch (error) {
      message.error("Erro ao fazer download do vídeo: " + error.message);
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDownloadCancel = () => {
    setDownloadModalVisible(false);
    setAvailableQualities([]);
    setSelectedQuality("");
    setDownloadingVideoId(null);
    setCurrentVideoName("");
  };

  const handleStopHlsConversion = () => {
    if (hlsConversionController && hlsConversionController.cleanup) {
      hlsConversionController.cleanup();
    }
    setIsHlsConverting(false);
    setHlsConversionProgress(0);
    setHlsConversionController(null);
    message.info("Conversão cancelada.");
  };

  const columns = [
    {
      title: "Id",
      dataIndex: "id",
      key: "id",
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => a.name.localeCompare(b.name),
    },
    {
      title: "Difficulty",
      dataIndex: "difficulty",
      key: "difficulty",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => a.difficulty.localeCompare(b.difficulty),
    },
    {
      title: "Transcoding",
      dataIndex: "transcoding",
      key: "transcoding",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => {
        const aValue = a.transcoding ? 1 : 0;
        const bValue = b.transcoding ? 1 : 0;
        return aValue - bValue;
      },
      render: (_, record) => (
        <Tag color={record.is_ready === true ? "green" : "red"}>
          {`${!record.is_ready}`}
        </Tag>
      ),
    },
    {
      title: "CreatedAt",
      dataIndex: "createdAt",
      key: "createdAt",
      sortDirections: ["ascend", "descend"],
      sorter: (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
      render: (_, record) => (
        <div>{moment(record?.createdAt).format("DD/MMM/YYYY")}</div>
      ),
    },
    {
      title: "Action",
      dataIndex: "",
      key: "x",
      render: (_, record) => (
        <div>
          <Row gutter={4}>
            {record.createdBy === user.email ||
            userLevels.includes(user.clearanceLevel) ? (
              <Col>
                <Button
                  type="primary"
                  onClick={() =>
                    openModal(
                      {
                        name: "Edit video",
                        confirmBtnText: "Update",
                        type: "editLocalVideoUpload",
                      },
                      record,
                    )
                  }
                >
                  <EditOutlined />
                </Button>
              </Col>
            ) : null}

            {user.clearanceLevel === "admin" ? (
              <Col>
                <Button
                  danger
                  onClick={() =>
                    openModal(
                      {
                        name: `Delete this video(${record.name})`,
                        confirmBtnText: "Delete",
                        type: "deleteVideo",
                      },
                      record,
                    )
                  }
                >
                  <DeleteOutlined />
                </Button>
              </Col>
            ) : null}

            {user.clearanceLevel === "admin" ? (
              <Col>
                <Button onClick={() => handleCopyLink(record)}>
                  <LinkOutlined />
                </Button>
              </Col>
            ) : null}

            <Col>
              <Button 
                onClick={() => {
                  console.log("🔍 [Download Debug] Download button clicked for record:", record);
                  handleDownloadClick(record);
                }}
                loading={downloadLoading && downloadingVideoId === record.id}
                disabled={downloadLoading}
              >
                <DownloadOutlined />
              </Button>
            </Col>
          </Row>
        </div>
      ),
    },
  ];

  return (
    <div>
      <div>
        <Row gutter={4}>
          <Col>
            {userLevels.includes(user.clearanceLevel) ? (
              <Button
                type="primary"
                onClick={() =>
                  openModal({
                    name: "Select type of upload",
                    confirmBtnText: "Confirm",
                    type: "createLocalUploadVideo",
                  })
                }
              >
                Create Video
              </Button>
            ) : null}
          </Col>

          <Col>
            <Search
              value={searchText}
              placeholder="Search here.."
              onSearch={onSearch}
              style={{ width: 200 }}
              onChange={handleSearchChange}
            />
          </Col>
        </Row>
      </div>

      {error ? (
        <div style={{ marginTop: 20 }}>
          {" "}
          <Alert message={error} type="error" />{" "}
        </div>
      ) : (
        ""
      )}

      <div style={{ marginTop: 20 }}>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={
              searchText && searchedResults.length > 0
                ? searchedResults
                : Array.isArray(videos)
                  ? videos
                  : []
            }
            rowKey="id"
          />
        </Spin>
      </div>

      <Modal
        title={modalInfo.name}
        open={isModalVisible}
        onOk={handleOk}
        onCancel={closeModal}
        footer={[
          <Button key="back" onClick={closeModal}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            danger={modalInfo.type === "deleteVideo"}
            loading={loading}
            onClick={handleOk}
          >
            {modalInfo.confirmBtnText}
          </Button>,
        ]}
      >
        {modalInfo.type === "createLocalUploadVideo" ||
        modalInfo.type === "editLocalVideoUpload" ? (
          <VideoForm
            difficulty={difficulty}
            name={name}
            tags={tags}
            tagIds={tagIds}
            videoLink={videoLink}
            videoId={videoId}
            time={time}
            thumbnail={thumbnail}
            handleInputChange={handleInputChange}
            changeThumbnail={changeThumbnail}
            thumbnailURL={thumbnailURL}
            handleSelectChanger={handleSelectChanger}
            pdfIds={pdfIds}
            pdfs={pdfs}
            videos={videos || []}
            customUpload={customUpload}
            videoUploadPayload={videoUploadPayload}
            description={description}
            mp4url={mp4url}
            muxUrl={muxUrl}
          />
        ) : null}
        {modalInfo.type === "deleteVideo" ? <DeleteForm name={name} /> : null}
      </Modal>

      <Modal
        title="Selecione a qualidade do download"
        open={downloadModalVisible}
        onOk={handleDownloadConfirm}
        onCancel={handleDownloadCancel}
        confirmLoading={downloadLoading}
        okText="Download"
        cancelText="Cancelar"
      >
        <div style={{ marginBottom: 16 }}>
          <p>Escolha a qualidade desejada para o download:</p>
          <Select
            value={selectedQuality}
            onChange={setSelectedQuality}
            style={{ width: "100%" }}
            placeholder="Selecione uma qualidade"
          >
            {availableQualities.map((quality) => (
              <Select.Option key={quality} value={quality}>
                {quality}
              </Select.Option>
            ))}
          </Select>
        </div>
      </Modal>

      {/* HLS Conversion Progress Modal */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>🔄 Convertendo vídeo HLS para MP4</span>
          </div>
        }
        open={isHlsConverting}
        footer={[
          <Button key="cancel" onClick={handleStopHlsConversion} danger>
            Cancelar Conversão
          </Button>
        ]}
        closable={false}
        maskClosable={false}
        width={500}
      >
        <div style={{ textAlign: 'center' }}>
          <div style={{ marginBottom: '16px' }}>
            <p style={{ fontSize: '16px', marginBottom: '8px' }}>
              {currentVideoName && (
                <strong>Vídeo: {currentVideoName}</strong>
              )}
            </p>
            <p style={{ color: '#666', marginBottom: '0' }}>
              {hlsConversionProgress === 0 
                ? 'Inicializando conversão...'
                : hlsConversionProgress < 10 
                ? 'Carregando vídeo HLS...'
                : hlsConversionProgress < 50
                ? 'Processando vídeo...'
                : hlsConversionProgress < 90
                ? 'Convertendo para MP4...'
                : 'Finalizando download...'
              }
            </p>
          </div>
          
          <Progress 
            percent={Math.round(hlsConversionProgress)} 
            status={isHlsConverting ? "active" : "success"}
            strokeColor={{
              '0%': '#1890ff',
              '50%': '#52c41a',
              '100%': '#87d068',
            }}
            trailColor="#f5f5f5"
            size={8}
          />
          
          <div style={{ marginTop: '16px' }}>
            <p style={{ fontSize: '14px', color: '#666', margin: '8px 0' }}>
              Progresso: {Math.round(hlsConversionProgress)}%
            </p>
            
            {hlsConversionProgress > 0 && hlsConversionProgress < 100 && (
              <div style={{ 
                backgroundColor: '#f0f2ff', 
                padding: '12px', 
                borderRadius: '6px',
                fontSize: '12px', 
                color: '#666',
                border: '1px solid #d6e4ff'
              }}>
                <p style={{ margin: '0 0 8px 0' }}>
                  ℹ️ <strong>Importante:</strong>
                </p>
                <p style={{ margin: '0' }}>
                  • Mantenha esta aba aberta durante a conversão<br/>
                  • O tempo pode variar dependendo do tamanho do vídeo<br/>
                  • O download iniciará automaticamente quando concluído
                </p>
              </div>
            )}
            
            {hlsConversionProgress === 100 && (
              <div style={{ 
                backgroundColor: '#f6ffed', 
                padding: '12px', 
                borderRadius: '6px',
                fontSize: '12px', 
                color: '#52c41a',
                border: '1px solid #b7eb8f'
              }}>
                ✅ Conversão concluída! Verificando download...
              </div>
            )}
          </div>
        </div>
      </Modal>

      {/* HLS Conversion Completion Notification */}
      {hlsConversionProgress === 100 && !isHlsConverting && (
        <div 
          style={{
            position: 'fixed',
            top: '20px',
            right: '20px',
            zIndex: 9999,
            background: '#52c41a',
            color: 'white',
            padding: '12px 20px',
            borderRadius: '6px',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
          }}
        >
          Conversão concluída! O download deve iniciar automaticamente.
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state) => ({
  response: state.videos.video || [],
  videos: state.videos.videos || [],
  tracks: state.tracks.tracks,
  error: state.videos.error,
  pdfs: state.pdfs.pdfs,
  tags: state.tags.tags,
  searchResult: state.tracks.searchResult,
  searchError: state.tracks.searchError,
});

export default connect(mapStateToProps, {
  createVideo,
  editVideo,
  deleteVideo,
  getVideos,
  fetchPdfs,
  fetchTracks,
  fetchTags,
  searchTrack,
  clearSearchResults,
  getVideoDownloadInfo,
  downloadVideo,
})(Videos);
